import { inject, Injectable, signal } from '@angular/core';
import { WINDOW } from '@src/app/providers/providers';

export interface DeviceInfo {
  [key: string]: any;
}

export interface GeoLocation {
  latitude: number;
  longitude: number;
}

@Injectable({
  providedIn: 'root',
})
export class DeviceInfoService {
  public deviceInfoData = signal<DeviceInfo>({});
  public geo = signal<GeoLocation | null>(null);
  public showGeoInfo = signal<boolean>(false);
  private window = inject(WINDOW);

  async checkData() {
    const battery = await ((navigator as any).getBattery?.() || Promise.resolve(null));
    this.geo.set(await this.getLocation());
    this.deviceInfoData.set({
      accept_language: navigator.languages || [],
      adblock: await this.detectAdBlock(),
      audio_hash: await this.getAudioHash(),
      battery_charging: battery?.charging ?? null,
      battery_level: battery?.level ? Math.round(battery.level * 100) : null,
      browser_hash: this.hashString(navigator.userAgent),
      browser_version: navigator.userAgent,
      browser: navigator.appName,
      canvas_hash: this.getCanvasHash(),
      cookie_enabled: navigator.cookieEnabled,
      cookie_hash: this.hashString(document.cookie || ''),
      device_hash: this.hashString(navigator.userAgent + screen.width + screen.height),
      device_memory: (navigator as any).deviceMemory || null,
      device_type: this.getDeviceType(),
      do_not_track: navigator.doNotTrack === '1',
      flash_enabled: false, // Flash is deprecated
      hardware_concurrency: navigator.hardwareConcurrency,
      java_enabled: navigator.javaEnabled?.() ?? false,
      os: navigator.platform,
      platform: navigator.platform,
      plugin_count: navigator.plugins?.length ?? 0,
      plugin_hash: this.hashString([...navigator.plugins].map((p) => p.name).join(',')),
      plugin_list: [...navigator.plugins].map((p) => p.name),
      region_language: navigator.language,
      region_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screen_available_resolution: `${screen.availWidth}x${screen.availHeight}`,
      screen_color_depth: screen.colorDepth,
      screen_pixel_ratio: this.window.devicePixelRatio,
      screen_resolution: `${screen.width}x${screen.height}`,
      session_id: this.generateSessionId(),
      source: document.referrer,
      touch_support: 'ontouchstart' in this.window || navigator.maxTouchPoints > 0,
      type: 'browser',
      user_agent: navigator.userAgent,
      webgl_hash: this.getWebGLHash(),
      webgl_vendor: this.getWebGLVendor(),
      webrtc_activated: typeof RTCPeerConnection !== 'undefined',
      window_size: `${this.window.innerWidth}x${this.window.innerHeight}`,
      private: false,
    });
  }

  private getLocation(): Promise<GeoLocation | null> {
    this.showGeoInfo.set(true);
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.showGeoInfo.set(false);
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        () => {
          this.showGeoInfo.set(false);
          resolve(null);
        },
      );
    });
  }

  private detectAdBlock(): Promise<boolean> {
    return new Promise((resolve) => {
      const bait = document.createElement('div');
      bait.className = 'adsbox ad-banner ad-unit ad-slot ad-container';
      bait.style.position = 'absolute';
      bait.style.height = '1px';
      bait.style.width = '1px';
      bait.style.left = '-9999px';
      document.body.appendChild(bait);
      setTimeout(() => {
        resolve(bait.offsetHeight === 0);
        document.body.removeChild(bait);
      }, 100);
    });
  }

  private getAudioHash(): Promise<string> {
    return new Promise((resolve) => {
      try {
        const ctx = new (this.window as any).webkitAudioContext();
        const oscillator = ctx.createOscillator();
        const analyser = ctx.createAnalyser();
        oscillator.type = 'triangle';
        oscillator.frequency.setValueAtTime(10000, ctx.currentTime);
        oscillator.connect(analyser);
        oscillator.start(0);
        setTimeout(() => {
          const data = new Uint8Array(analyser.frequencyBinCount);
          analyser.getByteFrequencyData(data);
          oscillator.stop();
          resolve(this.hashString(data.toString()));
        }, 200);
      } catch {
        resolve('');
      }
    });
  }

  private getCanvasHash(): string {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return '';
      ctx.textBaseline = 'top';
      ctx.font = "14px 'Arial'";
      ctx.fillText('Hello Canvas!', 2, 2);
      return this.hashString(canvas.toDataURL());
    } catch {
      return '';
    }
  }

  private getWebGLHash(): string {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') as WebGLRenderingContext;
      return this.hashString(gl?.getParameter(gl.VERSION) || '');
    } catch {
      return '';
    }
  }

  private getWebGLVendor(): string {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') as WebGLRenderingContext;
      const debugInfo = gl?.getExtension('WEBGL_debug_renderer_info');
      return debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : '';
    } catch {
      return '';
    }
  }

  private getDeviceType(): string {
    const ua = navigator.userAgent;
    if (/Mobi|Android/i.test(ua)) return 'mobile';
    if (/Tablet|iPad/i.test(ua)) return 'tablet';
    return 'desktop';
  }

  private generateSessionId(): string {
    return Math.random().toString(36).substring(2, 15);
  }

  private hashString(input: string): string {
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
      hash = (hash << 5) - hash + input.charCodeAt(i);
      hash |= 0;
    }
    return hash.toString();
  }
}
